import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ComicCard from "@/components/feature/ComicCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { comics, getComicById } from "@/mocks/comics";

export default function ComicDetail() {
  const { id } = useParams();
  const comic = getComicById(id || "");
  const [following, setFollowing] = useState(false);

  const related = useMemo(() => {
    if (!comic) return [];
    return comics
      .filter((c) => c.id !== comic.id && (c.category === comic.category || c.status === comic.status))
      .slice(0, 4);
  }, [comic]);

  if (!comic) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 py-24 text-center md:px-6">
        <i className="ri-file-search-line text-5xl text-secondary-400"></i>
        <h1 className="mt-4 text-xl font-bold text-foreground-950">没有找到这部漫画</h1>
        <p className="mt-2 text-sm text-foreground-600">它可能已被移除或链接有误。</p>
        <Link
          to="/list"
          className="mt-6 inline-flex items-center gap-1 rounded-full bg-primary-500 px-6 py-2.5 text-sm text-background-50 whitespace-nowrap hover:bg-primary-600"
        >
          返回列表 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    );
  }

  const isOngoing = comic.status === "连载中";

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://comicstatus.github.io/" },
              { "@type": "ListItem", position: 2, name: "漫画列表", item: "https://comicstatus.github.io/list" },
              { "@type": "ListItem", position: 3, name: comic.title, item: `https://comicstatus.github.io/comic/${comic.id}` },
            ],
          }),
        }}
      />

      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "漫画列表", to: "/list" },
          { label: comic.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        <div>
          <div className="overflow-hidden rounded-lg border border-background-200">
            <img src={comic.cover} alt={`${comic.title}封面 更新到第${comic.latestChapter}话`} title={`${comic.title} 追更进度查询`} className="w-full object-cover" />
          </div>
          <button
            onClick={() => setFollowing(!following)}
            className={`mt-4 flex w-full items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold whitespace-nowrap transition-colors ${
              following ? "bg-background-100 text-primary-600 border border-primary-300" : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
          >
            <i className={following ? "ri-heart-fill" : "ri-heart-add-line"}></i>
            {following ? "已加入追更" : "加入追更"}
          </button>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-3 py-1 text-xs text-background-50 ${isOngoing ? "bg-primary-500" : "bg-accent-500"}`}>
              {comic.status}
            </span>
            <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-700">{comic.category}</span>
            <span className="flex items-center gap-1 text-xs text-accent-700">
              <i className="ri-star-fill"></i> {comic.rating}
            </span>
          </div>

          <h1 className="mt-3 text-2xl font-bold text-foreground-950 md:text-3xl">{comic.title}</h1>

          <p className="prose-line mt-4 text-sm text-foreground-700">{comic.description}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            {comic.tags.map((t) => (
              <span key={t} className="rounded-full border border-secondary-200 bg-secondary-50 px-2.5 py-0.5 text-xs text-secondary-800">
                #{t}
              </span>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-background-100/70 p-3">
              <p className="text-xs text-secondary-500">最新章节</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">第{comic.latestChapter}话</p>
            </div>
            <div className="rounded-lg bg-background-100/70 p-3">
              <p className="text-xs text-secondary-500">{isOngoing ? "更新进度" : "总话数"}</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">
                {isOngoing ? `连载至${comic.latestChapter}话` : `全${comic.totalChapters}话`}
              </p>
            </div>
            <div className="rounded-lg bg-background-100/70 p-3">
              <p className="text-xs text-secondary-500">最近更新</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">{comic.updateTime}</p>
            </div>
            <div className="rounded-lg bg-background-100/70 p-3">
              <p className="text-xs text-secondary-500">人气</p>
              <p className="mt-1 text-sm font-semibold text-foreground-950">{comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}w` : comic.views}</p>
            </div>
          </div>
        </div>
      </div>

      {/* 章节列表 */}
      <section className="mt-12">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">
          {isOngoing ? "最新章节" : "全部章节"}
        </h2>
        <p className="mt-1 text-sm text-foreground-600">
          {isOngoing ? `连载中，更新到第${comic.latestChapter}话` : `已完结，共${comic.totalChapters}话`}
        </p>
        <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
          {comic.chapters.map((ch, i) => (
            <div
              key={ch.num}
              className={`flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-background-100 ${
                i !== comic.chapters.length - 1 ? "border-b border-background-200/70" : ""
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-background-100 text-xs font-semibold text-primary-600">
                  第{ch.num}话
                </span>
                <span className="text-sm text-foreground-900">{ch.title}</span>
              </div>
              <span className="whitespace-nowrap text-xs text-foreground-500">{ch.updateTime}</span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-xs text-secondary-500">* 章节列表为示例展示，更多往期章节请前往对应平台查看。</p>
      </section>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
          <p className="mt-1 text-sm text-foreground-600">同题材或同类状态，也许你会喜欢</p>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}